import { useEffect, useState, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTravelTranslation } from '../../app/TravelLocaleContext';
import { adsMemberApi, generateClientRequestId } from '../../api/adsMemberApi';
import './ads.css';

export default function CampaignCreatePage() {
  const { t } = useTravelTranslation();
  const navigate = useNavigate();
  const clientRequestIdRef = useRef(generateClientRequestId());

  const [promotions, setPromotions] = useState([]);
  const [creatives, setCreatives] = useState([]);
  const [loading, setLoading] = useState(true);

  const [promotionApplicationId, setPromotionApplicationId] = useState('');
  const [creativeId, setCreativeId] = useState('');
  const [startAt, setStartAt] = useState('');
  const [endAt, setEndAt] = useState('');
  const [budgetAmount, setBudgetAmount] = useState('1000');
  const [budgetCurrency, setBudgetCurrency] = useState('TWD');
  const [maxImpressionsPerMemberPerDay, setMaxDaily] = useState('10');
  const [maxImpressionsPerMemberPerSession, setMaxSession] = useState('3');
  const [minMinutesBetweenImpressions, setMinMinutes] = useState('30');
  const [targetingMode, setTargetingMode] = useState('ALL');
  const [targetLocationIdsStr, setTargetLocationIdsStr] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    Promise.all([
      adsMemberApi.promotions.listMine().catch(() => []),
      adsMemberApi.creatives.listMine().catch(() => []),
    ]).then(([promos, creats]) => {
      if (!active) return;
      const approvedPromos = (Array.isArray(promos) ? promos : []).filter(
        (p) => p.status === 'APPROVED',
      );
      const approvedCreats = (Array.isArray(creats) ? creats : []).filter(
        (c) => c.status === 'APPROVED',
      );
      setPromotions(approvedPromos);
      setCreatives(approvedCreats);

      if (approvedPromos.length > 0) {
        setPromotionApplicationId(approvedPromos[0].promotionApplicationId);
      }
      if (approvedCreats.length > 0) {
        setCreativeId(approvedCreats[0].creativeId);
      }

      // 預設設定 7 天後的排期
      const now = new Date();
      now.setMinutes(0, 0, 0);
      const nextWeek = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);

      const formatDt = (d) => {
        const pad = (n) => String(n).padStart(2, '0');
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
      };

      setStartAt(formatDt(now));
      setEndAt(formatDt(nextWeek));
      setLoading(false);
    });

    return () => {
      active = false;
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!promotionApplicationId) {
      setError('請選擇或填寫已核准的推廣申請 ID');
      return;
    }
    if (!creativeId) {
      setError('請選擇或填寫已核准的廣告素材 ID');
      return;
    }
    if (!startAt || !endAt) {
      setError('請設定開始與結束時間');
      return;
    }

    const startDate = new Date(startAt);
    const endDate = new Date(endAt);
    if (startDate >= endDate) {
      setError('開始時間必須早於結束時間');
      return;
    }

    const budget = Number(budgetAmount);
    if (!budget || budget <= 0) {
      setError('預算金額必須為正數');
      return;
    }

    setSubmitting(true);
    setError('');

    // ISO format for Spring LocalDateTime
    const formatIso = (str) => (str.length === 16 ? `${str}:00` : str);

    const locationIds = targetLocationIdsStr
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);

    try {
      const res = await adsMemberApi.campaigns.create({
        promotionApplicationId: String(promotionApplicationId),
        creativeId: String(creativeId),
        startAt: formatIso(startAt),
        endAt: formatIso(endAt),
        budgetAmount: budget,
        budgetCurrency: budgetCurrency.trim() || 'TWD',
        maxImpressionsPerMemberPerDay: Number(maxImpressionsPerMemberPerDay) || 10,
        maxImpressionsPerMemberPerSession: Number(maxImpressionsPerMemberPerSession) || 3,
        minMinutesBetweenImpressions: Number(minMinutesBetweenImpressions) || 30,
        targetingMode,
        targetLocationIds: locationIds,
        clientRequestId: clientRequestIdRef.current,
      });

      navigate(`/me/ads/campaigns/${res.campaignId}`);
    } catch (err) {
      setError(err?.message || '建立廣告活動失敗');
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <main className="ads-page" data-testid="campaign-create-loading">
        <div className="ads-loading">{t('travel.ads.common.loading')}</div>
      </main>
    );
  }

  return (
    <main className="ads-page" data-testid="campaign-create-page">
      <header className="ads-header">
        <h1 className="ads-title">{t('travel.ads.campaigns.create')}</h1>
        <Link to="/me/ads" className="ads-btn ads-btn--secondary">
          {t('travel.ads.common.backToAds')}
        </Link>
      </header>

      {error ? (
        <div className="ads-alert ads-alert--error" data-testid="campaign-create-error">
          {error}
        </div>
      ) : null}

      <form className="ads-form" onSubmit={handleSubmit} data-testid="campaign-create-form">
        <div className="ads-form-group">
          <label className="ads-form-label" htmlFor="promotionSelect">
            {t('travel.ads.campaigns.selectPromotion')}
          </label>
          {promotions.length > 0 ? (
            <select
              id="promotionSelect"
              className="ads-form-select"
              value={promotionApplicationId}
              onChange={(e) => setPromotionApplicationId(e.target.value)}
              disabled={submitting}
              data-testid="select-campaign-promotion"
            >
              {promotions.map((p) => (
                <option key={p.promotionApplicationId} value={p.promotionApplicationId}>
                  推廣 #{p.promotionApplicationId} (來源行程 #{p.sourceContentId})
                </option>
              ))}
            </select>
          ) : (
            <input
              id="promotionSelect"
              type="text"
              className="ads-form-input"
              value={promotionApplicationId}
              onChange={(e) => setPromotionApplicationId(e.target.value)}
              placeholder="請輸入已核准推廣申請 ID"
              disabled={submitting}
              data-testid="input-campaign-promotion-id"
            />
          )}
        </div>

        <div className="ads-form-group">
          <label className="ads-form-label" htmlFor="creativeSelect">
            {t('travel.ads.campaigns.selectCreative')}
          </label>
          {creatives.length > 0 ? (
            <select
              id="creativeSelect"
              className="ads-form-select"
              value={creativeId}
              onChange={(e) => setCreativeId(e.target.value)}
              disabled={submitting}
              data-testid="select-campaign-creative"
            >
              {creatives.map((c) => (
                <option key={c.creativeId} value={c.creativeId}>
                  {c.headline || `素材 #${c.creativeId}`}
                </option>
              ))}
            </select>
          ) : (
            <input
              id="creativeSelect"
              type="text"
              className="ads-form-input"
              value={creativeId}
              onChange={(e) => setCreativeId(e.target.value)}
              placeholder="請輸入已核准廣告素材 ID"
              disabled={submitting}
              data-testid="input-campaign-creative-id"
            />
          )}
        </div>

        <div className="ads-form-group">
          <label className="ads-form-label" htmlFor="startAt">
            {t('travel.ads.campaigns.startAt')}
          </label>
          <input
            id="startAt"
            type="datetime-local"
            className="ads-form-input"
            value={startAt}
            onChange={(e) => setStartAt(e.target.value)}
            disabled={submitting}
            data-testid="input-campaign-start-at"
          />
        </div>

        <div className="ads-form-group">
          <label className="ads-form-label" htmlFor="endAt">
            {t('travel.ads.campaigns.endAt')}
          </label>
          <input
            id="endAt"
            type="datetime-local"
            className="ads-form-input"
            value={endAt}
            onChange={(e) => setEndAt(e.target.value)}
            disabled={submitting}
            data-testid="input-campaign-end-at"
          />
        </div>

        <div className="ads-form-group">
          <label className="ads-form-label" htmlFor="budgetAmount">
            {t('travel.ads.campaigns.budget')}
          </label>
          <input
            id="budgetAmount"
            type="number"
            min="1"
            step="1"
            className="ads-form-input"
            value={budgetAmount}
            onChange={(e) => setBudgetAmount(e.target.value)}
            disabled={submitting}
            data-testid="input-campaign-budget"
          />
        </div>

        <div className="ads-form-group">
          <label className="ads-form-label" htmlFor="budgetCurrency">
            {t('travel.ads.campaigns.currency')}
          </label>
          <input
            id="budgetCurrency"
            type="text"
            className="ads-form-input"
            value={budgetCurrency}
            onChange={(e) => setBudgetCurrency(e.target.value)}
            disabled={submitting}
            data-testid="input-campaign-currency"
          />
        </div>

        <div className="ads-form-group">
          <label className="ads-form-label" htmlFor="maxDaily">
            每日頻次上限
          </label>
          <input
            id="maxDaily"
            type="number"
            min="1"
            className="ads-form-input"
            value={maxImpressionsPerMemberPerDay}
            onChange={(e) => setMaxDaily(e.target.value)}
            disabled={submitting}
            data-testid="input-campaign-max-daily"
          />
        </div>

        <div className="ads-form-group">
          <label className="ads-form-label" htmlFor="maxSession">
            每會話頻次上限
          </label>
          <input
            id="maxSession"
            type="number"
            min="1"
            className="ads-form-input"
            value={maxImpressionsPerMemberPerSession}
            onChange={(e) => setMaxSession(e.target.value)}
            disabled={submitting}
            data-testid="input-campaign-max-session"
          />
        </div>

        <div className="ads-form-group">
          <label className="ads-form-label" htmlFor="minMinutes">
            展示最小間隔 (分鐘)
          </label>
          <input
            id="minMinutes"
            type="number"
            min="0"
            className="ads-form-input"
            value={minMinutesBetweenImpressions}
            onChange={(e) => setMinMinutes(e.target.value)}
            disabled={submitting}
            data-testid="input-campaign-min-minutes"
          />
        </div>

        <div className="ads-form-group">
          <label className="ads-form-label" htmlFor="targetingMode">
            {t('travel.ads.campaigns.targetingMode')}
          </label>
          <select
            id="targetingMode"
            className="ads-form-select"
            value={targetingMode}
            onChange={(e) => setTargetingMode(e.target.value)}
            disabled={submitting}
            data-testid="select-campaign-targeting-mode"
          >
            <option value="ALL">{t('travel.ads.campaigns.targetingAll')}</option>
            <option value="LOCATION_MATCH">
              {t('travel.ads.campaigns.targetingLocation')}
            </option>
          </select>
        </div>

        {targetingMode === 'LOCATION_MATCH' ? (
          <div className="ads-form-group">
            <label className="ads-form-label" htmlFor="targetLocations">
              目標地點 ID (逗號分隔)
            </label>
            <input
              id="targetLocations"
              type="text"
              className="ads-form-input"
              value={targetLocationIdsStr}
              onChange={(e) => setTargetLocationIdsStr(e.target.value)}
              placeholder="例如: 101, 102"
              disabled={submitting}
              data-testid="input-campaign-locations"
            />
          </div>
        ) : null}

        <div>
          <button
            type="submit"
            className="ads-btn ads-btn--primary"
            disabled={submitting}
            data-testid="btn-submit-campaign-create"
          >
            {submitting ? t('travel.ads.common.submitting') : t('travel.ads.common.save')}
          </button>
        </div>
      </form>
    </main>
  );
}
